// Inline, dependency-free icons. Stroke-based so they inherit `currentColor`
// and stay crisp at the small header sizes. Kept minimalist/line-style to match
// the refreshed, uncluttered look.
export const ICONS = {
  // Back arrow — navigates to the conversations list ("page").
  conversations:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>',
  // Circular refresh — replaces the old trash icon; spins on hover.
  refresh:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="23 4 23 10 17 10"></polyline><polyline points="1 20 1 14 7 14"></polyline><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path></svg>',
  // Pencil/compose — the "new conversation" footer action.
  compose:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>',
  chevron:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"></polyline></svg>',
  // Minimalist sun / moon for the theme toggle.
  sun: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="4"></circle><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"></path></svg>',
  moon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path></svg>'
};

export function buildTemplate(config, t) {
  const brandingHTML = config.showBranding
    ? `<div class="branding">${t.poweredBy} <strong>Chatyx</strong> 🚀</div>`
    : '<div class="branding hidden"></div>';

  return `
<button type="button" class="chat-bubble" id="chatyx-bubble" aria-label="${t.openChat}" aria-haspopup="dialog" aria-expanded="false" aria-controls="chatyx-container">
  <img src="${config.bubbleIcon}" alt="">
  <span class="chatyx-unread-badge" id="chatyx-unread-badge" aria-live="polite"></span>
</button>
<div class="chat-container" id="chatyx-container" role="dialog" aria-modal="false" aria-label="${config.chatbotName}">
  <div class="chat-header-custom">
    <button type="button" class="chat-header-nav" id="chatyx-conversations-open" title="${t.viewConversations}" aria-label="${t.viewConversations}" aria-haspopup="dialog">${ICONS.conversations}</button>
    <div class="header-bot-avatar"><img src="${config.botAvatar}" alt=""></div>
    <div class="header-info-wrap">
      <span class="chatbot-title">${config.chatbotName}</span>
      <span class="bot-status"><span class="bot-status-online"></span>${t.online}</span>
    </div>
    <div class="chat-header-buttons">
      <button type="button" class="chat-header-button chat-theme-button" id="chatyx-theme-toggle" title="${t.darkMode}" aria-label="${t.darkMode}">${ICONS.moon}</button>
      <button type="button" class="chat-header-button chat-refresh-button" id="chatyx-new-conversation" title="${t.newConversationAction}" aria-label="${t.newConversationAction}" aria-haspopup="dialog">${ICONS.refresh}</button>
      <button type="button" class="chat-header-button chat-close-button" id="chatyx-close" title="${t.close}" aria-label="${t.close}"></button>
    </div>
  </div>

  <div class="consent-overlay" id="chatyx-consent" role="dialog" aria-modal="true" aria-labelledby="chatyx-consent-title">
    <div class="consent-content">
      <div class="consent-title" id="chatyx-consent-title">${t.privacyPolicyTitle}</div>
      <div class="consent-text">
        ${t.consentText}
        <a href="${config.privacyPolicyUrl}" target="_blank" rel="noopener noreferrer">${t.consentLinkText}</a>
        ${t.consentMore}
      </div>
      <div class="consent-checkbox-container">
        <input type="checkbox" id="chatyx-consent-checkbox" class="consent-checkbox">
        <label for="chatyx-consent-checkbox" class="consent-checkbox-label">
          ${t.consentCheckbox}
        </label>
      </div>
      <div class="consent-buttons">
        <button type="button" class="consent-button consent-decline" id="chatyx-consent-decline">${t.decline}</button>
        <button type="button" class="consent-button consent-accept" id="chatyx-consent-accept" disabled>${t.accept}</button>
      </div>
    </div>
  </div>

  <div class="chatyx-conversations-panel" id="chatyx-conversations" role="dialog" aria-modal="true" aria-label="${t.conversations}">
    <div class="chatyx-conversations-header">
      <span class="chatyx-conversations-title">${t.conversations}</span>
      <button type="button" class="chat-header-button chat-close-button chatyx-conversations-close" id="chatyx-conversations-close" title="${t.backToChat}" aria-label="${t.backToChat}"></button>
    </div>
    <div class="chatyx-conversations-list" id="chatyx-conversations-list" role="list"></div>
    <div class="chatyx-conversations-footer">
      <button type="button" class="chatyx-new-conversation-btn" id="chatyx-conversations-new">${ICONS.compose}<span>${t.newConversationAction}</span></button>
    </div>
  </div>

  <div class="chatyx-modal-overlay" id="chatyx-new-convo-modal" role="dialog" aria-modal="true" aria-labelledby="chatyx-new-convo-title">
    <div class="chatyx-modal-card">
      <div class="chatyx-modal-title" id="chatyx-new-convo-title">${t.newConversationPrompt}</div>
      <div class="chatyx-modal-actions">
        <button type="button" class="chatyx-modal-btn chatyx-modal-btn-primary" id="chatyx-new-convo-confirm">${t.newConversationAction}</button>
        <button type="button" class="chatyx-modal-btn chatyx-modal-btn-ghost" id="chatyx-new-convo-cancel">${t.cancel}</button>
      </div>
    </div>
  </div>

  <div class="chat-messages" id="chatyx-messages" role="log" aria-live="polite" aria-relevant="additions" aria-atomic="false" aria-label="${t.conversation}"></div>

  <div class="message-input">
    <input id="chatyx-input" placeholder="${t.inputPlaceholder}" autocomplete="off" aria-label="${t.inputPlaceholder}">
    <button type="button" id="chatyx-send" aria-label="${t.send}"><img src="${config.sendIcon}" alt=""></button>
  </div>
  <div class="ai-disclaimer" id="chatyx-ai-disclaimer">${t.aiDisclaimer}</div>
  ${brandingHTML}
</div>
`;
}

export function setupViewportSync(rootEl) {
  const syncViewportUnits = () => {
    const visualViewport = window.visualViewport;
    const viewportWidth = visualViewport ? visualViewport.width : window.innerWidth;
    const viewportHeight = visualViewport ? visualViewport.height : window.innerHeight;
    rootEl.style.setProperty('--chatyx-viewport-width', `${viewportWidth}px`);
    rootEl.style.setProperty('--chatyx-viewport-height', `${viewportHeight}px`);
  };

  syncViewportUnits();
  window.addEventListener('resize', syncViewportUnits, { passive: true });
  window.addEventListener('orientationchange', syncViewportUnits, { passive: true });
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', syncViewportUnits, { passive: true });
    window.visualViewport.addEventListener('scroll', syncViewportUnits, { passive: true });
  }

  return () => {
    window.removeEventListener('resize', syncViewportUnits);
    window.removeEventListener('orientationchange', syncViewportUnits);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', syncViewportUnits);
      window.visualViewport.removeEventListener('scroll', syncViewportUnits);
    }
  };
}

export function getWidgetElements(rootEl) {
  return {
    chatBubble: rootEl.querySelector('#chatyx-bubble'),
    chatWindow: rootEl.querySelector('#chatyx-container'),
    chatMessages: rootEl.querySelector('#chatyx-messages'),
    userInput: rootEl.querySelector('#chatyx-input'),
    sendButton: rootEl.querySelector('#chatyx-send'),
    closeButton: rootEl.querySelector('#chatyx-close'),
    newConversationButton: rootEl.querySelector('#chatyx-new-conversation'),
    themeToggleButton: rootEl.querySelector('#chatyx-theme-toggle'),
    conversationsOpenButton: rootEl.querySelector('#chatyx-conversations-open'),
    conversationsPanel: rootEl.querySelector('#chatyx-conversations'),
    conversationsList: rootEl.querySelector('#chatyx-conversations-list'),
    conversationsCloseButton: rootEl.querySelector('#chatyx-conversations-close'),
    conversationsNewButton: rootEl.querySelector('#chatyx-conversations-new'),
    newConvoModal: rootEl.querySelector('#chatyx-new-convo-modal'),
    newConvoConfirmButton: rootEl.querySelector('#chatyx-new-convo-confirm'),
    newConvoCancelButton: rootEl.querySelector('#chatyx-new-convo-cancel'),
    consentAcceptButton: rootEl.querySelector('#chatyx-consent-accept'),
    consentDeclineButton: rootEl.querySelector('#chatyx-consent-decline'),
    consentCheckbox: rootEl.querySelector('#chatyx-consent-checkbox'),
    unreadBadge: rootEl.querySelector('#chatyx-unread-badge')
  };
}
